/** P11 Market Outlook (screens design §2.3): the consensus with its ranges beside the realised path, at every point in the program. */
import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "../cockpit/cockpit.css";
import { briefItems } from "../cockpit/briefItems";
import { fiscalYearLabel } from "../format";
import { useRoute } from "../api/context";
import { useCohortData } from "../shell/data";
import { GxErrorBanner, GxWaiting } from "../theme/primitives";
import { Link } from "../shell/history";
import { OutlookBody } from "./OutlookBody";
import { pauseSettingOf } from "./pauseSignal";
import { markOutlookSeen, outlookRoundOf } from "./seen";
import { LEARNING_OUTLOOK_NOTE } from "../results/learningSurprise";

/** The eyebrow under the title: said the same everywhere the outlook shows (content.md owns the wording). */
export const OUTLOOK_NOTE = "This is the consensus view. The year that actually happens may differ, and rivals act on their own views.";

/** How long the Outlook may stay on its skeleton before it says so and offers to try again. */
const SLOW_AFTER_MS = 8_000;

export function OutlookPage({ cohortId, teamId }: { readonly cohortId: string; readonly teamId: string | null }) {
  const data = useCohortData(cohortId, teamId, 0);
  const outlook = useRoute("marketOutlook", { cohortId });
  const round = outlook.data?.asOfRound;
  const queryClient = useQueryClient();
  const waiting = data.status === "loading" || outlook.data === undefined;
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!waiting) { setSlow(false); return; }
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  const retry = () => { void outlook.refetch(); void queryClient.invalidateQueries({ queryKey: ["teamState"], refetchType: "all" }); };
  useEffect(() => { if (round !== undefined) markOutlookSeen(cohortId, outlookRoundOf(round)); }, [cohortId, round]); // the round being decided: the one the rail dot and the guide ask about
  if (data.error !== null) return <GxErrorBanner error={data.error} />;
  if (outlook.error !== null) return <GxErrorBanner error={outlook.error.result.error} />;
  if (data.status === "no-team") return <GxErrorBanner message="Your facilitator hasn't placed you on a team yet." />;
  if (data.status === "loading" || outlook.data === undefined) {
    return (
      <>
        {slow ? <GxErrorBanner message="The outlook is taking longer than usual." onRetry={retry} /> : null}
        <GxWaiting what="the market outlook" testId="outlook.skeleton" height={300} />
      </>
    );
  }
  const { team, catalog, market } = data;
  const changed = (team.round?.roundNo ?? 0) >= 2 ? briefItems(team, market, catalog, null, null).find((i) => i.key === "changed") : undefined;
  // Offered while the year is still being decided: the Outlook is the step before the decisions.
  const pauseSetting = pauseSettingOf(team.draft?.decisions.capital.buyback.pauseOnOutlook);
  const pauseProp = pauseSetting === undefined ? {} : { pauseSetting };
  const phase = team.round?.soloPhase;
  const deciding = team.round !== null && (phase === "deciding" || (phase == null && team.round.state === "open"));
  const toDecide = (testId: string) => <Link href={`/c/${cohortId}/decide`} className="gx-btn gx-btn--accent gx-btn--lg" data-testid={testId}>Go to your decisions →</Link>;
  return (
    <div className="gx-outlook-page" data-testid="outlook.page">
      <header className="gx-pagehead">
        <div>
          <div className="gx-eyebrow" data-testid="outlook.eyebrow">Round {outlook.data.asOfRound + 1} · {fiscalYearLabel(catalog, outlook.data.asOfRound + 1)} · As of start</div>
          <h1 className="gx-h1" style={{ marginTop: 4 }}>Market outlook</h1>
          <p className="gx-outlook-lead"><span data-testid="outlook.note">{OUTLOOK_NOTE}</span>{team.cohort.learningSurpriseActive === true ? <> <span data-testid="outlook.learning">{LEARNING_OUTLOOK_NOTE}</span></> : null}</p>
        </div>
        {deciding ? toDecide("outlook.go") : null}
      </header>
      <OutlookBody outlook={outlook.data} catalog={catalog} {...pauseProp} strip={changed === undefined ? undefined : <WhatChanged item={changed} />} />
      {deciding ? <div className="gx-outlook-foot">{toDecide("outlook.go.foot")}</div> : null}
    </div>
  );
}

function WhatChanged({ item }: { readonly item: { readonly body: ReactNode } }) {
  return (
    <section className="gx-card gx-brief gx-outlook-changed" data-testid="outlook.changed">
      <div className="gx-brief-lab"><span>What changed since last year</span></div>
      <div className="gx-brief-it">{item.body}</div>
    </section>
  );
}
